// Ported from cartadigital-pos app/domain/pricing/item-price.ts — keep in
// behavioral parity with backend OrderItemsAdapterService pricing.
import type { IMenuOption, IPosPricedItem } from '../pos-types';
import type { ISelectedOptionsMap } from '../item-options-validation';
import type { TaxDisplayMode } from '@/enums/TaxDisplayMode';
import { roundPrice } from '@/utils';
import { applyTax } from './tax';

export interface IComputeItemUnitPriceInput {
  item: IPosPricedItem;
  itemOptions: readonly IMenuOption[];
  selectedOptions: ISelectedOptionsMap;
  taxPercent: number | null | undefined;
  taxMode: TaxDisplayMode;
}

/**
 * Ported from backend pricing semantics (`OrderItemsAdapterService`).
 *
 * - Base starts as taxed item price (discountedPrice ?? price)
 * - For each selected option item:
 *   - optionTotal = taxed(optionItemCost) * val
 *   - if option.main -> base = optionTotal (replaces base)
 *   - else -> others += optionTotal
 * - Final unit price = roundPrice(base + others)
 */
export function computeItemUnitPrice(
  input: IComputeItemUnitPriceInput
): number {
  const baseRaw = input.item.discountedPrice ?? input.item.price;

  let base = applyTax({
    taxPercent: input.taxPercent,
    taxMode: input.taxMode,
    price: baseRaw,
    partial: true,
  });

  let others = 0;

  for (const option of input.itemOptions) {
    const selectedItems = input.selectedOptions[String(option.id)];
    if (!selectedItems) continue;

    for (const optionItem of option.items) {
      const rawVal = selectedItems[String(optionItem.id)];
      const val =
        typeof rawVal === 'number' && Number.isFinite(rawVal)
          ? Math.max(0, Math.trunc(rawVal))
          : 0;
      if (val <= 0) continue;

      const cost = optionItem.discountedCost ?? optionItem.cost;

      const unit = applyTax({
        taxPercent: input.taxPercent,
        taxMode: input.taxMode,
        price: cost,
        partial: true,
      });

      const total = unit * val;

      if (option.main) {
        base = total;
      } else {
        others += total;
      }
    }
  }

  return roundPrice(base + others);
}
